import React, { useContext, forwardRef } from 'react';
import styled from '@emotion/styled';
import { css } from '@emotion/react';
import { ButtonComponentRenderer } from '@agria/theme-brochureware/src/components/componentRenderer/ButtonComponentRenderer';
import { Button, ButtonProps } from '../button/Button';
import { Html } from '../html/Html';
import { Typography } from '../typography/Typography';
import { Stack } from '../stack/Stack';
import { Info } from '../icon';
import { useBackgroundColor } from '../../utils';
import { SectionContext } from '../../context/SectionContext';
import { Tag } from '../tag/Tag';

export interface PanelProps {
  tag?: string;
  heading?: string;
  headingSize?: 'Small' | 'Large' | '';
  extraInformation?: string;
  subHeading?: string;
  children?: any;
  content?: string;
  panelLink?: Array<{
    component: {
      link: {
        label: string;
        target: string | null;
        url: string;
      };
    };
  }>;
  background?: string;
}

export interface StyledPanelProps {
  background?: string;
}

export interface StyledExtraInformationProps {
  background?: string;
}

export interface StyledTagProps {
  background?: string;
}

export const StyledPanel = styled.div<StyledPanelProps>`
  ${({ theme: { space, colors, borderRadii, media }, background }) => css`
    padding: ${space.fluidLargeToXLarge} ${space.fluidSmallToXLarge};
    border-radius: ${borderRadii.medium};
    display: flex;
    flex-direction: column;
    gap: ${space.fluidMedium};
    height: 100%;
    justify-content: space-between;

    ${useBackgroundColor(background)}
  `};
`;

const StyledTag = styled(Tag)<StyledTagProps>`
  ${({ theme: { colors, space }, background }) => css`
    padding: ${space.fluidxxSmallToSmall} ${space.fluidSmall};
    width: fit-content;
    border-radius: 22px;
    ${useBackgroundColor(background)}
  `};
`;

const StyledHeadingStack = styled(Stack)`
  ${({ theme: { space, colors } }) => css`
    padding-bottom: ${space.large};
    border-bottom: solid 1px ${colors.brandOne20};
  `};
`;

const StyledExtraInformation = styled.div<StyledExtraInformationProps>`
  ${({ theme: { space, borderRadii, colors }, background }) => css`
    display: flex;
    align-items: center;
    border-radius: ${borderRadii.medium};
    gap: ${space.xxSmall};
    padding: ${space.small};
    background-color: ${colors.brandOne10};
    ${useBackgroundColor(background)}
  `};
`;

const StyledLinkContainer = styled.div`
  ${({ theme: { space, colors } }) => css`
    padding-top: ${space.large};
    border-top: solid 1px ${colors.brandOne20};
  `};
`;

// JSX
export const Panel = forwardRef<
  HTMLDivElement,
  React.PropsWithChildren<PanelProps>
>(
  (
    {
      tag,
      heading,
      subHeading,
      headingSize,
      children,
      content,
      extraInformation,
      panelLink,
      background,
      ...rest
    },
    ref
  ) => {
    const { sectionBackground } = useContext(SectionContext);

    return (
      <StyledPanel
        {...rest}
        ref={ref}
        background={
          sectionBackground === 'utilityWhite'
            ? 'utilityBackground'
            : 'utilityWhite'
        }
      >
        {tag && <StyledTag label={tag} background={sectionBackground} />}
        <Stack gap="fluidMedium">
          <StyledHeadingStack gap="small">
            {heading && (
              <Typography
                component="h2"
                variant={
                  headingSize === 'Small' ? 'headingMedium' : 'headingLarge'
                }
              >
                {heading}
              </Typography>
            )}
            {subHeading && (
              <Typography variant="bodyLarge">{subHeading}</Typography>
            )}
          </StyledHeadingStack>
          <Html>{children || content}</Html>
          {extraInformation && (
            <StyledExtraInformation background={sectionBackground}>
              <Info />
              <Typography as="span" variant="bodySmall">
                <Html>{extraInformation}</Html>
              </Typography>
            </StyledExtraInformation>
          )}
        </Stack>
        {panelLink && panelLink.length > 0 && (
          <StyledLinkContainer>
            <ButtonComponentRenderer components={[panelLink[0].component]} />
          </StyledLinkContainer>
        )}
      </StyledPanel>
    );
  }
);

Panel.displayName = 'Panel';

export default Panel;
